import type { ReactNode } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";

export interface SeriesGroupProps {
  /** Rótulo del grupo (una cuenta), en versalitas. */
  label: string;
  first?: boolean;
  children: ReactNode;
}

/** Rótulo de grupo sobre sus filas de serie. */
export function SeriesGroup({ label, first, children }: SeriesGroupProps) {
  return (
    <div className={first ? "mt-2" : "mt-4"}>
      <div className="text-[11px] font-medium uppercase leading-[14px] tracking-[0.14em] text-[var(--ds-ink-muted)]">{label}</div>
      {children}
    </div>
  );
}

export interface SeriesRowProps {
  label: string;
  /** Cualquier color CSS del marcador. */
  color: string;
  /** Forma del marcador: la serie no depende solo del color. */
  shape: "square" | "circle";
  /** Cifra principal ya formateada ("$1,249.95 / $24,999.00"). */
  amount: string;
  /** Qué significan las cifras ("Cuota mensual / saldo restante"). */
  caption?: string;
  /** Visible en las gráficas (aria-pressed). Si es false la fila baja a 60%. */
  shown: boolean;
  onToggleShown(): void;
  /** Nombre accesible del botón ojo ("Quitar Netflix de la gráfica"). */
  toggleLabel: string;
}

/** Fila de una serie de gráfica: marcador de forma, cifras y botón quitar / restaurar. */
export function SeriesRow({ label, color, shape, amount, caption, shown, onToggleShown, toggleLabel }: SeriesRowProps) {
  return (
    <div className={`grid grid-cols-[minmax(0,1fr)_auto_44px] items-center gap-x-3 border-t border-[var(--ds-line)] py-3 first:border-t-0 ${shown ? "" : "opacity-60"}`}>
      <b className="flex min-w-0 items-center gap-2 text-base font-medium leading-[22px] text-[var(--ds-ink)]">
        <i aria-hidden="true" className={`size-3 flex-none ${shape === "circle" ? "rounded-full" : "rounded-[3px]"}`} style={{ background: color }} />
        <span className="truncate">{label}</span>
      </b>
      <span className="text-right text-base font-medium leading-[22px] text-[var(--ds-ink)]">
        {amount}
        {caption && <small className="block text-xs font-normal leading-4 text-[var(--ds-ink-muted)]">{caption}</small>}
      </span>
      <button
        type="button"
        aria-label={toggleLabel}
        aria-pressed={shown}
        onClick={onToggleShown}
        className="app-focus-ring flex size-11 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 text-[var(--ds-ink)]"
      >
        {shown ? <FiEye aria-hidden="true" className="size-[22px]" /> : <FiEyeOff aria-hidden="true" className="size-[22px]" />}
      </button>
    </div>
  );
}
